import { Drawer as ChakraDrawer, HStack, Portal, Separator, Stack } from '@chakra-ui/react';
import { ChevronLeft, X } from 'lucide-react';
import * as React from 'react';

import { IconButton } from '../button/icon-button';

export interface DrawerContentProps extends Omit<ChakraDrawer.ContentProps, 'asChild'> {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
  backdrop?: boolean;
}

const DrawerContent = React.forwardRef<HTMLDivElement, DrawerContentProps>(function DrawerContent(props, ref) {
  const { children, portalled = true, portalRef, backdrop = true, ...rest } = props;

  return (
    <Portal disabled={!portalled} container={portalRef}>
      {backdrop && <ChakraDrawer.Backdrop />}
      <ChakraDrawer.Positioner>
        <ChakraDrawer.Content ref={ref} {...rest} asChild={false}>
          {children}
        </ChakraDrawer.Content>
      </ChakraDrawer.Positioner>
    </Portal>
  );
});

const DrawerCloseTrigger = React.forwardRef<HTMLButtonElement, ChakraDrawer.CloseTriggerProps>(
  function DrawerCloseTrigger(props, ref) {
    const { children, ...rest } = props;

    if (children) {
      return (
        <ChakraDrawer.CloseTrigger ref={ref} {...rest} asChild>
          {children}
        </ChakraDrawer.CloseTrigger>
      );
    }

    return (
      <ChakraDrawer.CloseTrigger {...rest} asChild>
        <IconButton aria-label="Close" icon={X} ref={ref} size="rg" variant="ghost" colorPalette="neutral" />
      </ChakraDrawer.CloseTrigger>
    );
  }
);

export interface DrawerHeaderProps extends ChakraDrawer.HeaderProps {
  /** Renders a back-arrow button on the left. */
  onBack?: () => void;
  /** Content rendered on the right side of the header (e.g. filter button, badges). */
  endElement?: React.ReactNode;
  /** Show the close (X) button in the header. Defaults to `false`. */
  closable?: boolean;
}

const DrawerHeader = React.forwardRef<HTMLDivElement, DrawerHeaderProps>(function DrawerHeader(props, ref) {
  const { children, onBack, endElement, closable = false, ...rest } = props;

  return (
    <ChakraDrawer.Header ref={ref} {...rest}>
      <HStack gap="2" width="full" align="center">
        {onBack && (
          <IconButton
            aria-label="Back"
            icon={ChevronLeft}
            size="rg"
            variant="ghost"
            colorPalette="neutral"
            onClick={onBack}
          />
        )}
        <Stack gap="1" flex="1" minW="0">
          {children}
        </Stack>
        {endElement}
        {closable && (
          <ChakraDrawer.CloseTrigger asChild position="relative" top="0" insetInlineEnd="0">
            <IconButton aria-label="Close" icon={X} size="rg" variant="ghost" colorPalette="neutral" />
          </ChakraDrawer.CloseTrigger>
        )}
      </HStack>
    </ChakraDrawer.Header>
  );
});

const DrawerFooter = React.forwardRef<HTMLDivElement, ChakraDrawer.FooterProps>(function DrawerFooter(props, ref) {
  const { children, ...rest } = props;

  return (
    <ChakraDrawer.Footer ref={ref} {...rest}>
      <Stack gap="4" width="full">
        <Separator alignSelf="stretch" borderColor="neutral.200" />
        <HStack gap="2" justifyContent="flex-end" width="full">
          {children}
        </HStack>
      </Stack>
    </ChakraDrawer.Footer>
  );
});

export const Drawer = {
  Root: ChakraDrawer.Root,
  Content: DrawerContent,
  Header: DrawerHeader,
  Title: ChakraDrawer.Title,
  Description: ChakraDrawer.Description,
  Body: ChakraDrawer.Body,
  Footer: DrawerFooter,
  CloseTrigger: DrawerCloseTrigger,
  Trigger: ChakraDrawer.Trigger,
  ActionTrigger: ChakraDrawer.ActionTrigger,
  Backdrop: ChakraDrawer.Backdrop,
};
